'use client';

/**
 * global-error — the last line of defence (A1).
 *
 * This boundary catches errors thrown in the ROOT layout itself, so Next renders
 * it INSTEAD of app/layout.tsx — it must supply its own <html>/<body> and cannot
 * rely on the app's fonts/providers being mounted. Because of that it inlines the
 * canonical dark tokens rather than importing globals.css chrome, so a broken
 * root still paints the private-wealth terminal look (never a white blank crash).
 *
 * Nested route errors are handled by the per-group error.tsx files, which DO get
 * the full themed shell. This file only fires when the shell itself is the thing
 * that broke.
 */

import { useEffect } from 'react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Surface to the server logs / error tracker. Kept minimal — no PII.
    // eslint-disable-next-line no-console
    console.error('global-error boundary', error);
  }, [error]);

  return (
    <html lang="en-AU">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#0b0e11',
          color: '#e8edf2',
          fontFamily: "'Geist', ui-sans-serif, system-ui, sans-serif",
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        <div style={{ maxWidth: 460, textAlign: 'center' }}>
          <BullMark />
          <h1
            style={{
              margin: '20px 0 0',
              fontFamily: "'Instrument Serif', ui-serif, Georgia, serif",
              fontSize: 30,
              fontWeight: 400,
              lineHeight: 1.15,
              color: '#e8edf2',
            }}
          >
            Something broke on our side
          </h1>
          <p
            style={{
              margin: '10px 0 0',
              fontSize: 14,
              lineHeight: 1.6,
              color: '#8a97a6',
            }}
          >
            The page failed to load. This has been logged. You can retry, or head back to the
            terminal — nothing you did caused this.
          </p>
          {error.digest ? (
            <p
              style={{
                margin: '14px 0 0',
                fontFamily: "'Geist Mono', ui-monospace, monospace",
                fontSize: 11,
                letterSpacing: '0.04em',
                color: '#5c6773',
              }}
            >
              ref {error.digest}
            </p>
          ) : null}
          <div
            style={{
              marginTop: 24,
              display: 'flex',
              gap: 10,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => reset()}
              style={{
                height: 40,
                padding: '0 20px',
                borderRadius: 4,
                border: 'none',
                background: '#6ea8fe',
                color: '#0b0e11',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            {/*
              Raw <a> (not next/link) is deliberate: global-error replaces the
              root layout when the app shell itself has crashed, so the router
              may be unmounted. A hard navigation is the robust way back home.
            */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              style={{
                height: 40,
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0 20px',
                borderRadius: 4,
                border: '1px solid #2c353f',
                background: 'transparent',
                color: '#e8edf2',
                fontSize: 14,
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              Back to home
            </a>
          </div>
          <p style={{ margin: '20px 0 0', fontSize: 12, color: '#5c6773' }}>
            If this keeps happening, contact{' '}
            <a href="mailto:support@stocksinglobal.com.au" style={{ color: '#6ea8fe' }}>
              support@stocksinglobal.com.au
            </a>
            .
          </p>
        </div>
      </body>
    </html>
  );
}

/**
 * SIG bull mark — a compact, single-stroke bull silhouette in steel. Inlined
 * (no shared import) because global-error renders outside the app shell. Kept
 * geometric and calm to match the terminal aesthetic; decorative only.
 */
function BullMark() {
  return (
    <svg
      width="56"
      height="56"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      style={{ display: 'block', margin: '0 auto' }}
    >
      <circle cx="24" cy="24" r="23" stroke="#232b33" strokeWidth="1.5" />
      <path
        d="M14 18c-2.6-1.2-4-3.2-4.4-5.6C11.8 13.6 14 15 16 16.6c1.2-.9 2.7-1.5 4.3-1.7 1.3-.15 2.5-.15 3.7 0 1.6.2 3.1.8 4.3 1.7 2-1.6 4.2-3 6.4-4.2-.4 2.4-1.8 4.4-4.4 5.6.7 1.2 1 2.6 1 4 0 4.6-3.7 8.3-8.6 8.3S14 26.6 14 22c0-1.4.3-2.8 1-4Z"
        stroke="#6ea8fe"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="20.5" cy="22.5" r="1.1" fill="#6ea8fe" />
      <circle cx="27.5" cy="22.5" r="1.1" fill="#6ea8fe" />
    </svg>
  );
}
